import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AppRole } from "@/contexts/AuthContext";
import { anyRoleAlwaysOn } from "@/lib/moduleAccessOverrides";

interface ModuleVisibilityRow {
  module_key: string;
  role: string;
  visible: boolean;
}

// Hardcoded fallback matching original AppSidebar roles arrays
const FALLBACK_VISIBILITY: Record<string, string[]> = {
  dashboard: [],
  admission: ["superadmin", "principal", "deputy_principal", "head_admission"],
  administration: ["superadmin", "principal", "deputy_principal", "head_administration"],
  academic: ["superadmin", "principal", "deputy_principal", "head_academic", "hod_cmt", "hod_pst", "hod_sw", "it_incharge", "student"],
  examination: ["superadmin", "principal", "deputy_principal", "head_examination", "hod_cmt", "hod_pst", "hod_sw", "it_incharge", "student"],
  accounting: ["superadmin", "principal", "deputy_principal", "head_accounting", "student"],
  student_services: ["superadmin", "principal", "deputy_principal", "head_student_service", "student"],
  discipline: ["superadmin", "principal", "deputy_principal", "head_discipline", "student"],
  pre_registration: ["superadmin"],
  communication: ["superadmin"],
  user_management: ["superadmin"],
  audit_log: ["superadmin", "principal", "deputy_principal"],
  attendance: ["superadmin", "principal", "deputy_principal", "head_administration", "head_academic", "it_incharge", "security_man", "hod_cmt", "hod_pst", "hod_sw", "cr_cmt4", "cr_cmt5", "cr_cmt6", "cr_pst4", "cr_pst5", "cr_pst6", "cr_sw4", "cr_sw5", "cr_sw6"],
  result_visibility: ["superadmin"],
  chitchat: [],
};

const MODULE_KEY_TO_URL: Record<string, string> = {
  dashboard: "/dashboard",
  admission: "/admission",
  administration: "/administration",
  academic: "/academic",
  examination: "/examination",
  accounting: "/accounting",
  student_services: "/student-services",
  discipline: "/discipline",
  pre_registration: "/student-pre-registration",
  communication: "/communication",
  user_management: "/user-management",
  attendance: "/attendance",
  audit_log: "/audit-log",
  chitchat: "/chitchat",
};

const isAllowedByFallback = (moduleKey: string, role: AppRole): boolean => {
  const allowedRoles = FALLBACK_VISIBILITY[moduleKey];
  if (!allowedRoles) return false;
  if (allowedRoles.length === 0) return true;
  return allowedRoles.includes(role);
};

export function useModuleVisibility(roles: AppRole[]) {
  const { data: visibilityRows, isLoading: queryLoading } = useQuery({
    queryKey: ["module-visibility"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("module_visibility")
        .select("module_key, role, visible");
      if (error) throw error;
      return data as ModuleVisibilityRow[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const isModuleVisible = (moduleKey: string): boolean => {
    // Hard-wired overrides win over both DB rows and fallback lists.
    if (anyRoleAlwaysOn(moduleKey, roles as unknown as string[])) {
      return true;
    }

    if (!visibilityRows || visibilityRows.length === 0) {
      return roles.some((role) => isAllowedByFallback(moduleKey, role));
    }

    // Check if any DB rows exist for this specific module
    const moduleRows = visibilityRows.filter(v => v.module_key === moduleKey);
    if (moduleRows.length === 0) {
      // No DB config for this module yet — use fallback
      return roles.some((role) => isAllowedByFallback(moduleKey, role));
    }

    return roles.some(role => {
      const row = moduleRows.find(v => v.role === role);
      return row ? row.visible : isAllowedByFallback(moduleKey, role);
    });
  };

  const isUrlVisible = (url: string): boolean => {
    const entry = Object.entries(MODULE_KEY_TO_URL).find(([, u]) => u === url);
    if (!entry) return true;
    return isModuleVisible(entry[0]);
  };

  return { isModuleVisible, isUrlVisible, isLoading: queryLoading };
}

export { MODULE_KEY_TO_URL };
